/**
 * CameraRigSystem
 * 
 * Runtime system for CameraRig components.
 * Handles camera following, damping, noise, and all Cinemachine-like behaviors.
 * 
 * Can be used both in editor preview and player runtime.
 */

import type { Level } from './Level';
import type { GameObject } from './GameObject';
import type { GUID } from './types';
import type { CameraRig } from './components/CameraRig.component';
import {
  CameraRigBehaviour,
  GameObjectEngineContext,
  type GameObjectContextDeps,
} from './scripting';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Result of evaluating a camera rig for a frame. (Per-rig runtime STATE now lives inside the
 * CameraRigBehaviour; the engine system only selects the active rig + drives it.)
 */
export interface CameraRigOutput {
  /** World position X */
  x: number;
  /** World position Y */
  y: number;
  /** Rotation in radians */
  rotation: number;
  /** Orthographic size */
  orthoSize: number;
}

/**
 * Options for the camera rig system
 */
export interface CameraRigSystemOptions {
  /** Enable debug logging */
  debug?: boolean;
  /** Aspect ratio for target group calculations (default: 16/9) */
  aspectRatio?: number;
}

// ============================================================================
// CAMERA RIG SYSTEM
// ============================================================================

/**
 * Manages CameraRig components and their runtime behavior.
 * 
 * Usage:
 * ```typescript
 * const system = new CameraRigSystem();
 * 
 * // In your update loop:
 * const output = system.update(level, deltaTime);
 * if (output) {
 *   camera.setPosition(output.x, output.y);
 *   camera.setRotation(output.rotation);
 *   camera.orthoSize = output.orthoSize;
 * }
 * ```
 */
export class CameraRigSystem {
  /** Cached per-rig CameraRigBehaviour instances (the scripting-system camera logic). */
  private _behaviours = new Map<GUID, CameraRigBehaviour>();

  /** Currently active rig GUID */
  private activeRig: GUID | null = null;
  /** Script-forced active rig (engine.camera.setActive); null = automatic selection. */
  private _forcedRig: GUID | null = null;
  
  /** Total time elapsed */
  private time = 0;
  
  /** Options */
  private options: CameraRigSystemOptions;
  
  constructor(options: CameraRigSystemOptions = {}) {
    this.options = options;
  }
  
  /**
   * Reset all state (call when level changes)
   */
  reset(): void {
    this._behaviours.clear();
    this.activeRig = null;
    this.time = 0;
  }
  
  /**
   * Update all camera rigs and return the output for the active camera.
   * 
   * @param level Current level
   * @param deltaTime Time since last frame (seconds)
   * @returns Camera output to apply, or null if no active rig
   */
  update(level: Level, deltaTime: number): CameraRigOutput | null {
    this.time += deltaTime;
    
    // Find all CameraRig components
    const rigs = this.findRigs(level);
    if (rigs.length === 0) {
      this.activeRig = null;
      return null;
    }
    

    
    // Determine active rig based on priority/solo
    const activeRigResult = this.determineActiveRig(rigs);
    if (!activeRigResult) {
      this.activeRig = null;
      return null;
    }
    
    // Store active rig GUID for external access
    this.activeRig = activeRigResult.gameObject.guid;
    
    // The active rig's logic runs as a CameraRigBehaviour (the scripting system); it writes the
    // transform itself, so update() returns null and updateCameraRigs handles the Camera2D
    // dirty-bump + editor reactivity for the active rig.
    this.runBehaviour(level, activeRigResult.gameObject, deltaTime);
    return null;
  }

  /** Drive the rig's CameraRigBehaviour (cached) through a per-frame engine context. */
  private runBehaviour(level: Level, go: GameObject, deltaTime: number): void {
    let behaviour = this._behaviours.get(go.guid);
    if (!behaviour) {
      behaviour = new CameraRigBehaviour();
      this._behaviours.set(go.guid, behaviour);
    }
    const time = this.time;
    const deps: GameObjectContextDeps = {
      resolve: (id) => level.findById(id),
      resolveGuid: (guid) => level.findByGuid(guid)?.id ?? null,
      time: { time, deltaTime, fixedDeltaTime: 1 / 60 },
      // Camera logic doesn't read input; provide a no-op cap to satisfy the contract.
      input: { getInput: () => 0, isInputPressed: () => false, isInputDown: () => false, isInputUp: () => false },
    };
    behaviour.__bind(new GameObjectEngineContext(go, deps));
    behaviour.onUpdate(deltaTime);
  }

  /**
   * Find all GameObjects with CameraRig component
   */
  private findRigs(level: Level): Array<{ gameObject: GameObject; rig: CameraRig }> {
    const result: Array<{ gameObject: GameObject; rig: CameraRig }> = [];
    
    for (const go of level.getAllObjects()) {
      const rig = go.getComponentData('core.CameraRig');
      if (rig) {
        result.push({ gameObject: go, rig });
      }
    }
    
    return result;
  }
  
  /**
   * Determine which rig should be active based on priority and solo mode
   */
  private determineActiveRig(
    rigs: Array<{ gameObject: GameObject; rig: CameraRig }>
  ): { gameObject: GameObject; rig: CameraRig } | null {
    // Script override (engine.camera.setActive / Camera2D.makeMain) wins if it matches a rig.
    if (this._forcedRig) {
      const forced = rigs.find(r => r.gameObject.guid === this._forcedRig);
      if (forced) return forced;
    }
    // First check for solo mode
    const soloRig = rigs.find(r => r.rig.solo);
    if (soloRig) {
      return soloRig;
    }
    
    // Filter to only rigs using priority
    const priorityRigs = rigs.filter(r => r.rig.usePriority);
    
    if (priorityRigs.length === 0) {
      // No priority rigs, return first one
      return rigs[0] ?? null;
    }
    
    // Sort by priority (highest first), then by channel
    priorityRigs.sort((a, b) => {
      if (a.rig.channel !== b.rig.channel) {
        return a.rig.channel - b.rig.channel;
      }
      return b.rig.priority - a.rig.priority;
    });
    
    return priorityRigs[0] ?? null;
  }
  
  // ==========================================================================
  // GETTERS
  // ==========================================================================
  
  /** Get currently active rig GUID */
  get activeRigGuid(): GUID | null {
    return this.activeRig;
  }

  /** Script override: force a specific rig entity (by GUID) active, or `null` to clear. */
  setActiveRig(guid: GUID | null): void {
    this._forcedRig = guid;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

/** Default camera rig system instance */
export const cameraRigSystem = new CameraRigSystem();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Update camera rig system and apply output to transform
 * 
 * @param level Current level
 * @param deltaTime Delta time in seconds
 * @returns The camera output if a rig was active, or null
 */
export function updateCameraRigs(level: Level, deltaTime: number, triggerUpdate?: () => void): CameraRigOutput | null {
  // The active rig runs as a CameraRigBehaviour, which writes its own transform + ortho size.
  cameraRigSystem.update(level, deltaTime);
  const activeGuid = cameraRigSystem.activeRigGuid;
  if (!activeGuid) return null;

  // Finalize the active camera: bump the Camera2D dirty version (so the renderer picks up
  // orthoSize changes) + fire editor reactivity. The transform was already written.
  const go = level.findByGuid(activeGuid);
  if (go) {
    const cameraId = go.getComponent('core.Camera2D');
    if (cameraId !== null) {
      const cameraState = go.getComponentState(cameraId);
      if (cameraState?.version !== undefined) {
        cameraState.version++;
      }
    }
    triggerUpdate?.();
  }
  return null;
}

// ============================================================================
// ENGINE SYSTEM INTEGRATION
// ============================================================================

import type { ISystem } from './Engine';
import { levelManager } from './LevelManager';

/**
 * Create an ISystem-compatible object for the camera rig system.
 * Register this with Engine.registerSystem() to enable automatic updates.
 * 
 * @param getLevel - Optional function to get the current level (defaults to levelManager.activeLevel)
 * @param getDeltaTime - Function to get delta time (required, e.g. () => engine.time.deltaTime)
 * 
 * @example
 * ```typescript
 * import { engine, createCameraRigEngineSystem } from '@engine/core';
 * 
 * // During initialization
 * engine.registerSystem(createCameraRigEngineSystem({
 *   getDeltaTime: () => engine.time.deltaTime,
 * }));
 * ```
 */
export function createCameraRigEngineSystem(options?: {
  getLevel?: () => Level | null;
  getDeltaTime?: () => number;
}): ISystem {
  const getLevel = options?.getLevel ?? (() => levelManager.activeLevel);
  const getDeltaTime = options?.getDeltaTime ?? (() => 1 / 60); // Default to 60fps
  
  return {
    name: 'CameraRig',
    
    update(): void {
      const level = getLevel();
      if (!level) return;
      
      const dt = getDeltaTime();
      updateCameraRigs(level, dt);
    },
  };
}
